// @flow
import flattenDeep from "lodash/flattenDeep";

import { isPlainObject } from "../utils/helpers/objects";

import { setSettings } from "../constants/Settings";

import type { Theme } from "./types";

const replacePlaceholders = (inputTheme: Theme) => {
  const possibleKeys = Object.keys(inputTheme)
    .map((key) => `\\b${key}\\b`)
    .join("|");

  const re = new RegExp(`\\$(${possibleKeys})`, "g");
  Object.entries(inputTheme).forEach(([key, value]) => {
    if (key !== "settings" && typeof value === "string") {
      inputTheme[key] = value.replace(
        re,
        // $FlowIssue[incompatible-call]
        (match, capture) => inputTheme[capture],
      );
    }
  });

  return inputTheme;
};

const generateTheme = (storedTheme: Theme, ...themeElements: Array<Theme>) => {
  const storedSettings = storedTheme?.settings ?? {};

  let generatedTheme = { settings: {} };
  for (const element of themeElements) {
    if (typeof element === "function") {
      generatedTheme = element(replacePlaceholders(generatedTheme));
    } else if (isPlainObject(element)) {
      const generatedSettings = generatedTheme.settings;
      // $FlowFixMe[cannot-spread-indexer]
      generatedTheme = { ...generatedTheme, ...element, ...storedTheme };
      if ("settings" in element) {
        generatedTheme.settings = {
          ...generatedSettings,
          ...element.settings,
          ...storedSettings,
        };
      }
    }
  }

  // final - replace any assignments inside the theme
  return replacePlaceholders(generatedTheme);
};

/**
 * Create a theme from multiple theme configuration objects.
 * Properties are overwriten by iterating the configuration objects from left to right.
 *
 * The first argument represents a stored theme part, this is optional. The stored theme part will overwrite every part in the collection of theme parts.
 * Thus a stored configuration is leading as it can be configured by a modeller through the repository.
 *
 * Placeholders can be used, they are replaced before calling a function theme part or at the end of the theme creation.
 *
 * Both objects and function can be used, where a function receives the previous objects as input arguments.
 * For example using the method createTheme(ThemeObject, ThemeObject2, ThemeFunction, ThemeFunction2), will be handled as:
 *
 * ThemeFunction2(ThemeFunction1({ ...ThemeObject, ...ThemeObject2 }))
 *
 * @example
 * Example where the primary color of the default theme is overwritten with a primary color of the custom theme,
 * but the button bg still uses the yiq function of the default theme:
 *
 * const DefaultTheme = {
 *   PRIMARY_COLOR: "#0000ff",
 *   SECONDARY_COLOR: "#00ff00",
 *   BUTTON_COLOR: "$PRIMARY_COLOR"
 * }
 *
 * const createDefaultTheme = (input) => ({
 *   ...input,
 *   BUTTON_BG: getContrastYIQ(input.BUTTON_COLOR)
 * })
 *
 * const CustomTheme = {
 *   PRIMARY_COLOR: "#ff0000"
 * }
 *
 * createTheme(null, DefaultTheme, CustomTheme, createDefaultTheme);
 *
 * // Result:
 * {
 *   PRIMARY_COLOR: "#ff0000",
 *   BUTTON_COLOR: "#ff0000",
 *   BUTTON_BG: "#fff",
 *   SECONDARY_COLOR: "#00ff00",
 * }
 */
const createTheme = (
  storedTheme: Theme,
  ...themeConfigs: Array<Theme | Array<Theme> | void>
):
  | any
  | {}
  | {
      INPUT_FOCUS_BORDER_COLOR?: string,
      LINK_HOVER_COLOR?: string,
      PAGING_HOVER_COLOR?: string,
      settings?: { [settingName: string]: string },
    } => {
  const customTheme = generateTheme(storedTheme, ...flattenDeep(themeConfigs));

  if (customTheme) {
    setSettings(customTheme.settings);
  }

  return customTheme;
};

export default createTheme;
